import { getBackendUrl } from '../config/urlAuthority';
import { TRUTH_MARKER } from '../utils/truthMarker';

// PKG-NET-001.1: Single Network Primitive Surface
// This is the ONLY module permitted to invoke 'fetch'

const API_BASE = getBackendUrl().replace(/\/$/, '');
const base = API_BASE;

/**
 * Canonical Request Wrapper
 * Enforces unified error handling, logging, and truth markers.
 */
async function request(path, options = {}) {
  const url = path.startsWith('http') ? path : `${base}${path}`;

  const module = path.includes('investors') ? 'INVESTORS' :
    path.includes('kb') ? 'KB' :
      path.includes('startups') ? 'STARTUPS' : 'SYSTEM';

  TRUTH_MARKER('DATA_REQUEST_START', module, 'PASS');
  console.info(`[API] ${options.method || 'GET'} ${url}`);

  try {
    const isFormData = options.body instanceof FormData;
    const response = await fetch(url, {
      ...options,
      headers: isFormData
        ? { ...options.headers }
        : { 'Content-Type': 'application/json', ...options.headers },
    });

    if (!response.ok) {
      TRUTH_MARKER('BACKEND_RESPONSE', module, 'FAIL');
      // Extract error body for taxonomy-aware error messages
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorClass = null;
      try {
        const errorBody = await response.json();
        // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055): FastAPI wraps a
        // raised HTTPException's payload as `{detail: ...}`, so a STRUCTURED
        // detail arrives as an OBJECT. Assigning it straight to Error() rendered
        // the literal string "[object Object]" to the CEO. Unwrap the
        // business-facing `message` and carry `error_class` alongside — the
        // friendly sentence and the machine-readable class travel together
        // ("no silent masking"), never one without the other.
        if (errorBody.message) errorMessage = errorBody.message;
        else if (errorBody.detail && typeof errorBody.detail === 'object') {
          errorMessage = errorBody.detail.message || errorMessage;
          errorClass = errorBody.detail.error_class || null;
        } else if (errorBody.detail) errorMessage = errorBody.detail;
      } catch (_) { /* response body not JSON — use status text */ }
      const err = new Error(errorMessage);
      err.status = response.status;
      err.errorClass = errorClass;
      throw err;
    }

    TRUTH_MARKER('BACKEND_RESPONSE', module, 'PASS');
    const data = await response.json();

    TRUTH_MARKER('FRONTEND_ACCEPTANCE', module, 'PASS');
    return data;
  } catch (error) {
    TRUTH_MARKER('FRONTEND_ACCEPTANCE', module, 'FAIL');
    throw error;
  }
}

// Exported for modules that need custom POST/PATCH calls through canonical wrapper
export { request };

export const getJSON = (path, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`${path}${qs ? `?${qs}` : ''}`);
};

// Startups API
export const getStartups = (params) => getJSON('/api/startups', params); // noscan
export const getStartupCounts = () => getJSON('/api/startups/counts'); // noscan
export const getStartup = (id) => getJSON(`/api/startups/${id}`);
export const getStartupFirstLook = (id) => getJSON(`/api/startups/${encodeURIComponent(id)}/first-look`); // R06: the saved first look + call material
export const createStartup = (data) => request('/api/startups', { method: 'POST', body: JSON.stringify(data) }); // noscan
export const updateStartup = (id, data) => request(`/api/startups/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteStartup = (id) => updateStartup(id, { is_active: false });

// Cross-Sector Pattern Agent API (PKG-068)
export const getCrossPatternConfig    = () => getJSON('/api/cross-sector-patterns/config');
export const getCrossPatternsPending  = () => getJSON('/api/cross-sector-patterns/pending');
export const getCrossPatternsAccepted = () => getJSON('/api/cross-sector-patterns/accepted');
export const getCrossPatternsRejected = () => getJSON('/api/cross-sector-patterns/rejected');
export const agreeCrossPattern        = (id, rating) => request(`/api/cross-sector-patterns/${id}/feedback`, { method: 'POST', body: JSON.stringify({ action: 'agree', rating }) });
export const overrideCrossPattern     = (id, reason) => request(`/api/cross-sector-patterns/${id}/feedback`, { method: 'POST', body: JSON.stringify({ action: 'override', reason }) });

// Principle Library Agent API (WI-PRINCIPLE-LIBRARY-GOVERNANCE-BUILD-001)
// GTC Link-1: handler_name="handleFeedback" in PrincipleLibraryAgent.jsx calls submitPrincipleFeedback.
export const getPrincipleLibraryConfig   = () => getJSON('/api/principle-library/config');
export const getPrincipleLibraryPending  = () => getJSON('/api/principle-library/pending');
export const getPrincipleLibraryAccepted = () => getJSON('/api/principle-library/accepted');
export const getPrincipleLibraryRejected = () => getJSON('/api/principle-library/rejected');
export const submitPrincipleFeedback     = (id, action, extra = {}) => request(`/api/principle-library/${id}/feedback`, { method: 'POST', body: JSON.stringify({ action, ...extra }) });

// Sector-Review CEO card API (#923 accept route, WI-INFRA-UPGRADE-CLOSEOUT-BUNDLE-001 Leg D)
// GTC Link-1: StartupReviewDetail.jsx's Accept/Reject buttons call submitSectorReviewFeedback.
// This is the ONLY writer of the canonical taxonomy fields onto Collections.STARTUPS —
// backend/routes/startup_intelligence.py::submit_sector_review_feedback. action is "accept" | "reject".
export const submitSectorReviewFeedback  = (startupId, action) => request(`/api/intelligence/sector-review/${startupId}/feedback`, { method: 'POST', body: JSON.stringify({ action }) });

// Founders API
export const getFounders = (params) => getJSON('/api/founders', params); // noscan
export const getFounder = (id) => getJSON(`/api/founders/${id}`);
export const createFounder = (data) => request('/api/founders', { method: 'POST', body: JSON.stringify(data) }); // noscan
export const updateFounder = (id, data) => request(`/api/founders/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteFounder = (id) => updateFounder(id, { is_active: false });

// Tools API
export const getTools = (params) => getJSON('/api/tools', params);
export const getTool = (id) => getJSON(`/api/tools/${id}`);
export const createTool = (data) => request('/api/tools', { method: 'POST', body: JSON.stringify(data) });
export const updateTool = (id, data) => request(`/api/tools/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteTool = (id) => request(`/api/tools/${id}`, { method: 'DELETE' });

// Investors API
export const getInvestors = (params) => getJSON('/api/investors', params); // noscan
export const getInvestorById = (id) => getJSON(`/api/investors/${id}`);
export const getInvestorCounts = () => getJSON('/api/investors/counts');
export const createInvestor = (data) => request('/api/investors', { method: 'POST', body: JSON.stringify(data) }); // noscan
export const updateInvestor = (id, data) => request(`/api/investors/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteInvestor = (id) => updateInvestor(id, { is_active: false });

// KB API
export const getKB = (params) => getJSON('/api/kb', params);
export const getKBEntries = (params) => getJSON('/api/kb', params);
export const getKBCounts = () => getJSON('/api/kb/counts');
export const getKBEntry = (id) => getJSON(`/api/kb/${id}`);
export const createKBEntry = (data) => request('/api/kb', { method: 'POST', body: JSON.stringify(data) });
export const updateKBEntry = (id, data) => request(`/api/kb/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteKBEntry = (id) => request(`/api/kb/${id}`, { method: 'DELETE' });

// KB Conviction API (WI-DESIGN-IMPL-001-KB Phase 2)
export const patchKBConviction = (id, status, reason) =>
  request(`/api/kb/${id}/conviction`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
export const getKBConvictionHistory = (id) => getJSON(`/api/kb/${id}/conviction-history`);
export const reExamineKBEntry = (id) =>
  request(`/api/kb/${id}/re-examine`, { method: 'POST', body: JSON.stringify({}) });

// Strategy Insights API (WI-DESIGN-IMPL-001 Phase 2)
export const getStrategyInsights = () => getJSON('/api/strategy-insights');
export const rerunStrategyInsightsSection = (section) =>
  request(`/api/strategy-insights/${encodeURIComponent(section)}/rerun`, {
    method: 'POST',
  });

// Inbox seven-tile summary (WI-DESIGN-IMPL-001 Phase 2)
export const getWorkSummary = () => getJSON('/api/work/summary');

// The CEO's idea queue — what he approved that is still waiting for a draft.
// Surfaces existing linkedin_posts records at status=triggered; no new store.
// Throws on failure: the page must be able to tell "nothing waiting" apart from
// "we could not look", and a swallowed error makes those two identical.
export const getIdeaQueue = () => getJSON('/api/linkedin-posts/ideas');

// NOTE for the next reader: the idea-card decision clients ALREADY EXIST further down this
// file — `decideIdeaCard` (approve/promote, POST /inbox/{id}/card-decision) and
// `rejectCampaignTask` (reject, POST /inbox/{id}/reject). The Ideas tab's row controls reuse
// those rather than adding a second pair. I wrote a duplicate `decideIdeaCard` here first and
// the build caught it as a redeclaration; leaving this pointer so nobody repeats that.
// WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001.

// Post Studio redraft (WI-DESIGN-IMPL-001 Phase 2)
export const redraftLinkedInPost = (postId) =>
  request(`/api/linkedin-posts/${encodeURIComponent(postId)}/redraft`, {
    method: 'POST',
  });

// R24 (content stream): send a draft back for a versioned rewrite, with a writing reason attached. His own reject stays final.
export const sendBackLinkedInPost = (postId, reason, note) =>
  request(`/api/linkedin-posts/${encodeURIComponent(postId)}/send-back`, {
    method: 'POST',
    body: JSON.stringify({ reason, note: note || null }),
  });

// Ingestion API
export const ingestArticle = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  // Custom fetch for multipart
  return fetch(`${base}/api/agent/ingest/article`, {
    method: 'POST',
    body: formData,
  }).then(res => res.ok ? res.json() : res.text().then(t => { throw new Error(t); }));
};

// Inbox & Review API
export const getInbox = () => getJSON('/api/review/inbox');
export const acceptReviewItem = (id) => request(`/api/review/${id}/accept`, { method: 'POST' });
export const rejectReviewItem = (id, rationale) => request(`/api/review/${id}/reject`, { method: 'POST', body: JSON.stringify({ rationale }) });
export const deferReviewItem = (id) => request(`/api/review/${id}/defer`, { method: 'POST' });
// Missing Exports Polyfill (Phase 22 Fix)
export const getStartupInbox = async () => {
  // Robust wrapper: Fetch full inbox, safe filter
  try {
    const items = await getInbox();
    return Array.isArray(items) ? items.filter(i => i.source_type === 'startup_lead') : [];
  } catch (e) {
    console.warn("getStartupInbox failed, returning empty", e);
    return [];
  }
};
export const getKBInbox = async () => {
  try {
    const items = await getInbox();
    return Array.isArray(items) ? items.filter(i => i.source_type === 'kb_article') : [];
  } catch (e) {
    console.warn("getKBInbox failed, returning empty", e);
    return [];
  }
};
export const seedReviewBuffer = () => request('/api/debug/seed_review_buffer', { method: 'POST' });

// Article Agent API (Phase 4+)
export const ingestArticleMulti = (articles, inputChannel = 'manual_upload') =>
  request('/api/articles/ingest-multi', {
    method: 'POST',
    body: JSON.stringify({ articles, input_channel: inputChannel }),
  });
// WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13 — one click analyses ONLY the
// articles that click was for. `articleIds` is the set of `agent_articles._id`
// values the just-completed ingest produced (duplicates come back with
// `article_id: null` and are correctly absent), or, for Resume Analysis, every
// row currently waiting.
//
// THE ARGUMENT MUST BE SENT, NOT MERELY ACCEPTED. Before this, the function took
// ZERO parameters while both call sites had already been updated to pass ids —
// and JavaScript drops a surplus argument silently, with no error. The result was
// the screen truthfully reporting "Analyzing 1 article" while the request went out
// bodyless and the backend drained the whole pending queue: the reporting surface
// fixed and the behaviour unchanged, which hides the defect instead of closing it
// (the ratified "wrong surface reports cleanly and is wrong" class, P-2014).
// docs/findings/ONE_LINK_ONE_ARTICLE_2026-08-12.md is the measurement behind it.
//
// An empty or missing list is sent as `{}` deliberately, so it reaches the route's
// NAMED business-facing refusal (`no_article_ids_provided`) rather than being
// papered over here. There is no client-side fallback to "process everything" —
// that path was removed from the route, not defaulted away.
export const processArticles = (articleIds) =>
  request('/api/articles/process', {
    method: 'POST',
    body: JSON.stringify(
      articleIds && articleIds.length ? { article_ids: articleIds } : {}
    ),
  });
export const getArticleProcessingStatus = () =>
  request('/api/articles/processing-status');
export const getArticleStatus = () =>
  request('/api/articles/status');
export const getPendingArticles = () =>
  request('/api/articles/pending');
export const checkEmailForArticles = (sourceSenders) =>
  request('/api/articles/check-email', {
    method: 'POST',
    body: JSON.stringify(sourceSenders ? { source_senders: sourceSenders } : {}),
  });
export const saveArticleToKB = (articleId, ceoCalibration) =>
  request(`/api/articles/${articleId}/save`, {
    method: 'POST',
    body: JSON.stringify(ceoCalibration ? { ceo_calibration: ceoCalibration } : {}),
  });
export const rejectArticle = (articleId, reason, ceoCalibration) =>
  request(`/api/articles/${articleId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...(ceoCalibration ? { ceo_calibration: ceoCalibration } : {}) }),
  });
export const overrideArticle = (articleId, reason, ceoCalibration) =>
  request(`/api/articles/${articleId}/override`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...(ceoCalibration ? { ceo_calibration: ceoCalibration } : {}) }),
  });
export const getArticleRejectionReasons = () =>
  getJSON('/api/articles/rejection-reasons');
export const getArticleOverrideReasons = () =>
  getJSON('/api/articles/override-reasons');
// DEPRECATED: Use ingestArticleMulti instead
export const ingestArticleBulk = (rawText, inputChannel = 'manual_upload') =>
  request('/api/articles/ingest-bulk', {
    method: 'POST',
    body: JSON.stringify({ raw_text: rawText, input_channel: inputChannel }),
  });

// Article Draft Persistence (Task 6.2)
export const getArticleDrafts = () =>
  getJSON('/api/articles/drafts');
export const createArticleDraft = (data) =>
  request('/api/articles/drafts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
export const updateArticleDraft = (draftId, data) =>
  request(`/api/articles/drafts/${draftId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
export const deleteArticleDraft = (draftId) =>
  request(`/api/articles/drafts/${draftId}`, { method: 'DELETE' });
// `confirm=true` is REQUIRED by the route as of 2026-08-26 — without it the call is refused 400
// (WI-TA-CLOSE-BULK-DELETE-AUTH-2026-08-26). This is a bulk `delete_many({})` over article_drafts,
// and the guard exists so a bare call cannot empty the collection by accident. It is NOT a
// credential and is not pretending to be one; the caller-authentication gap on this route is
// carded as P-1034 / P-1039.
export const deleteAllArticleDrafts = () =>
  request('/api/articles/drafts?confirm=true', { method: 'DELETE' });

// Article Retry/Remove/Failed (Phase 10c)
export const retryArticle = (articleId) =>
  request(`/api/articles/${articleId}/retry`, { method: 'PATCH' });
export const removeArticle = (articleId) =>
  request(`/api/articles/${articleId}`, { method: 'DELETE' });
export const getFailedArticles = () =>
  getJSON('/api/articles/failed');
export const getRecentActivity = () =>
  getJSON('/api/articles/recent-activity');

// System & Meta API
export const getSystemIdentity = () => getJSON('/api/system/identity');
export const getSystemStatus = () => getJSON('/api/admin/status/latest');
export const getBuildVersion = () => fetch('/version.json').then(res => res.json());

// Legacy/Compatibility fetch (use with caution, internally ONLY)
export const rawFetch = (url, options) => fetch(url, options);

// Canonical Inbox Gateway
// FAILURE IS NOT SILENT ANY MORE (WI-POST-STUDIO-PENDING-VIEW-AND-ONE-CARD-001 Leg 0b,
// CEO ruling 2026-07-30).
//
// This used to `catch` everything, log "[UnifiedInbox] Critical Failure" to a console the CEO
// never opens, and RETURN `{items: []}`. The cockpit then rendered that as
// "Inbox is clear. Nothing waiting on you right now." — stating as fact that nothing was
// waiting. OBSERVED LIVE three times in one sitting on the deployed site while the endpoint
// held TWO items, one of them the failed-draft receipt he had been waiting on. The backend
// runs on a suspending free tier, so the first request after an idle period fails; that is
// not an edge case here, it is the normal morning.
//
// Same defect P-97 named on the COUNTERS (`getInboxSummary` returning `{}` → seven confident
// zeros), fixed there by throwing and never fixed on the LIST. The list matters more: a wrong
// count is a wrong number, an empty list is a claim that there is no work.
//
// The consumer was already waiting for the truth — `useInboxCockpitState.refresh` settles this
// and the summary independently and has an `else` branch that sets "Could not load inbox. Try
// again." It simply could never fire. Removing the swallow lights up a path that already
// exists; no new machinery.
//
// A genuinely empty inbox still returns an empty list. Distinguishing "no work" from "no
// answer" is the whole point — turning every empty into an error would be the opposite failure.
export const getCanonicalInbox = async (cursor = null, limit = 50) => {
  // Phase 43: Unified Work Inbox (Backend Aggregation + Pagination)
  // Authority: WORK_FEED_CONTRACT.md
  const params = { limit };
  if (cursor) params.cursor = cursor;

  // Throws on network failure and on a non-OK status — `request` already does both.
  const data = await getJSON('/api/work/inbox', params); // noscan

  // A 200 carrying a body we cannot read is still "the question was not answered", so it is
  // raised rather than coerced into an empty list.
  if (!data || !Array.isArray(data.items)) {
    throw new Error(
      'The inbox came back in a shape we could not read, so nothing is being shown. '
      + 'This is not the same as having no work waiting.',
    );
  }

  return {
    items: data.items,
    next_cursor: data.next_cursor || null,
    has_more: !!data.has_more,
  };
};

// Cockpit summary tiles — WI-UI-CLEANUP-001 Stage 2.
// Backend source: backend/routes/work_inbox.py::_compute_tile_counts.
// Shape: { strong, weak, pass, directional, oldest, overnight, kb_writes }
// each tile is { count: number, delta?: number, items_ref?: any }.
//
// PATH: the route is declared `@router.get("/summary")` in work_inbox.py and
// the router is mounted at prefix `/api/work` in server.py — so the served
// path is `/api/work/summary`. This client asked for `/api/work/inbox/summary`
// from 2026-04-22 until 2026-07-29 and got a 404 every single time. It never
// worked; there is no regression here, only a defect that was invisible
// because the 404 was swallowed and rendered as seven zeros.
//
// FAILURE IS NOT SILENT ANY MORE. This throws on failure instead of returning
// {}. Returning {} made an unreachable endpoint indistinguishable on screen
// from a genuinely empty inbox — the CEO read "0 STRONG, 0 WEAK, 0 PASS" as
// fact while three cards sat in review. The caller
// (useInboxCockpitState) catches it and the cockpit says so on screen.
export const getInboxSummary = async () => {
  const data = await getJSON('/api/work/summary'); // noscan
  if (!data || typeof data !== 'object') {
    throw new Error('The summary came back in a shape we could not read.');
  }
  return data;
};

// Campaign / DevTask API (New Phase 13)
// DEAD EXPORT — /api/dev/tasks route not registered. Do not call.
// Authority: PKG-016 scan 2026-04-15. Remove in next cleanup pass.
export const getCampaignInbox = () => request('/api/dev/tasks?status=proposal_ready&category=campaign&environment=prod'); // noscan
// Phase 61: Canonical Command Routes
export const approveCampaignTask = (id, payload) => request(`/api/work/inbox/${id}/accept`, { method: 'POST', body: JSON.stringify({ user_id: 'user_v1', payload: payload || {} }) });
// WI-LINKEDIN-IDEA-GATE-OBSERVE-BUILD-001: `attribution` is an optional 4th arg
// (message_wrong | writing_wrong, LinkedIn-post rejects only) — additive, omitted
// from the payload entirely when not supplied so every existing caller/behavior
// is unchanged. Backend: work_inbox.py ActionRequest.payload is Dict[str, Any].
// WI-IDEA-CARD-BUILD-001 Leg 2: `words` is a further-additive optional 5th arg —
// the CEO's own free text on a pre-draft idea card reject (idea_card.ceo_decision_words).
export const rejectCampaignTask = (id, rationale, calibration, attribution, words) => request(`/api/work/inbox/${id}/reject`, {
  method: 'POST',
  body: JSON.stringify({
    user_id: 'user_v1',
    payload: {
      rationale,
      ...(calibration ? { calibration } : {}),
      ...(attribution ? { attribution } : {}),
      ...(words ? { words } : {}),
    },
  }),
});
// R09 (CEO row-set approval, 20 Oct 2026): a DIFFERENT idea from the same article, by his commission only. The idea he turned
// down stays turned down; this makes a new post from the original article.
export const commissionDifferentIdea = (postId, direction) => request(`/api/linkedin-posts/${postId}/commission-different-idea`, {
  method: 'POST',
  body: JSON.stringify(direction ? { direction } : {}),
});
// R11 (CEO row-set approval, 20 Oct 2026): a card that could not be finished is shown as needing repair under the same post id;
// this is his "Try again" (the existing recompose door, run for real on that one post).
export const repairIdeaCard = (postId) => request(`/api/admin/idea-cards/${postId}/recompose?execute=true`, { method: 'POST' });
export const deferCampaignTask = (id, duration = '1_day') => request(`/api/work/inbox/${id}/defer`, { method: 'POST', body: JSON.stringify({ user_id: 'user_v1', payload: { duration } }) });
// WI-IDEA-CARD-BUILD-001 Leg 2: CEO decision (approve/promote) on a pre-draft
// idea card. Reject continues through rejectCampaignTask above — this route
// carries the two decisions that dispatcher does not handle. `reason` is a
// structured LINKEDIN_POST_REJECT_TAXONOMY id (required for promote, optional
// for approve); `words` is the CEO's own free text (optional, additive).
export const decideIdeaCard = (id, decision, reason, words) => request(`/api/work/inbox/${id}/card-decision`, {
  method: 'POST',
  body: JSON.stringify({
    user_id: 'user_v1',
    payload: {
      decision,
      ...(reason ? { reason } : {}),
      ...(words ? { words } : {}),
    },
  }),
});
// WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — the three verb endpoints a work-item card's
// available_verbs can offer beyond approve/reject/promote/defer/acknowledge. Built AHEAD of
// the backend landing them (a sibling lane builds the routes in the same session) — the
// contract below is agreed, not observed, so every caller is proven only against a mocked
// @/lib/api in tests, never against a live network call.
//
// CORRECTED (same window, second commit): every POST /api/work/inbox/{id}/... route in
// backend/routes/work_inbox.py takes the house `ActionRequest` envelope
// ({user_id: str, payload: Dict}) — see approveCampaignTask/rejectCampaignTask/decideIdeaCard
// above, all of which already wrap their body this way. The first version of these three
// callers sent a FLAT body, which fails against that shape twice over: FastAPI 422s on the
// missing required user_id, and even with one present the fields sit outside `payload`,
// where every existing handler reads them (`payload = request.payload or {}`) — so the
// handler would see an empty payload. The backend is following the SAME convention as
// every sibling route; this fixes the frontend to match it, not the other way round.
// `user_id: 'user_v1'` matches the existing house callers above — there is no other
// established source for it in this file.
//
// REPAIR — the CEO edits the exact sentence a gate objected to (original -> replacement) and
// can ask for an immediate re-evaluation in the same call, rather than a second round trip.
export const repairAnnotation = (itemId, annotationIndex, original, replacement, reEvaluate = true) =>
  request(`/api/work/inbox/${encodeURIComponent(itemId)}/annotation-repair`, {
    method: 'POST',
    body: JSON.stringify({
      user_id: 'user_v1',
      payload: {
        annotation_index: annotationIndex,
        original,
        replacement,
        re_evaluate: reEvaluate,
      },
    }),
  });

// OVERRULE — the CEO keeps the line as written despite the objection. `words` is his own
// optional free text. The backend records the overrule; it never removes the objection it
// was recorded against — the disagreement is the data, and the frontend must keep rendering
// the annotation after this call succeeds rather than treating the overrule as a dismissal.
export const overruleAnnotation = (itemId, annotationIndex, words = null) =>
  request(`/api/work/inbox/${encodeURIComponent(itemId)}/annotation-overrule`, {
    method: 'POST',
    body: JSON.stringify({
      user_id: 'user_v1',
      payload: {
        annotation_index: annotationIndex,
        words: words || null,
      },
    }),
  });

// RE-EVALUATE — re-run the card's gate check, either as the direct follow-up to a repair
// ('after_repair') or a bare retry the CEO asked for himself ('manual_retry').
export const reEvaluateWorkItem = (itemId, trigger = 'manual_retry') =>
  request(`/api/work/inbox/${encodeURIComponent(itemId)}/re-evaluate`, {
    method: 'POST',
    body: JSON.stringify({
      user_id: 'user_v1',
      payload: { trigger },
    }),
  });

export const removeScoutCandidate = (taskId, candidateId) =>
    request(`/api/work/inbox/${taskId}/remove-candidate`, {
        method: 'POST',
        body: JSON.stringify({
            user_id: 'user_v1',
            payload: { candidate_id: candidateId },
        }),
    });
export const executeScoutBatch = (taskId) =>
    request('/api/lead-gen/scout/execute', {
        method: 'POST',
        body: JSON.stringify({ task_id: taskId }),
    });
export const logScoutOutcome = (taskId, candidateId, outcome, error = '') =>
    request('/api/lead-gen/scout/log-outcome', {
        method: 'POST',
        body: JSON.stringify({ task_id: taskId, candidate_id: candidateId, outcome, error }),
    });
export const exportScoutBatch = (taskId) =>
    getJSON(`/api/lead-gen/scout/${taskId}/export`);


// LinkedIn Post Agent API
export const getApprovedLinkedInPosts = () =>
  getJSON('/api/linkedin-posts/approved');
export const getLinkedInDraftCount = () =>
  getJSON('/api/linkedin-posts/draft/count'); // noscan
export const getPublishedLinkedInPosts = () =>
  getJSON('/api/linkedin-posts/published');
export const publishLinkedInPost = (postId, ceoCalibration) =>
  request(`/api/linkedin-posts/${postId}/publish`, {
    method: 'POST',
    body: JSON.stringify(ceoCalibration ? { ceo_calibration: ceoCalibration } : {}),
  });
export const triggerLinkedInPostPipeline = () =>
  request('/api/linkedin-posts/process', { method: 'POST' });
export const getLinkedInPostPipelineStatus = () =>
  getJSON('/api/linkedin-posts/status');
export const regenerateLinkedInPost = (postId, modifiers) =>
  request(`/api/linkedin-posts/${postId}/regenerate`, {
    method: 'POST',
    body: JSON.stringify({ modifiers }),
  });
export const saveLinkedInPostEdits = (postId, postText) =>
  request(`/api/linkedin-posts/${postId}/edit`, {
    method: 'PATCH',
    body: JSON.stringify({ post_text: postText }),
  });

// Deal Evaluator API (backend routes unchanged)
// DEAD EXPORT — /api/deal-evaluator/* routes not registered. Do not call.
// Authority: PKG-016 scan 2026-04-15. Remove in next cleanup pass.
export const getDealEvaluatorStartups = (params) =>
  getJSON('/api/deal-evaluator/startups', params); // noscan
export const searchStartups = (query) =>
  getJSON('/api/startups/search', { q: query });
// uploadDealEvaluatorDeck — REMOVED. Dead export deleted per DS_INPUT_TRIGGER_REDESIGN_2026-06-02.
// Route /api/deal-evaluator/upload-deck was never registered (PKG-016 scan 2026-04-15).
export const getFCA = (startupId) =>
  getJSON(`/api/pitch-deck/${startupId}/fca`);
export const getFCAStatus = (startupId) =>
  getJSON(`/api/pitch-deck/${startupId}/status`);

// Deal Screen API
export const getDealScreensRecent = (limit) =>
  getJSON('/api/deal-screen/recent', limit ? { limit } : {});
export const searchDealScreens = (query) =>
  getJSON('/api/deal-screen/search', { q: query });
export const rerunDealScreen = (startupId, modifiers) =>
  request(`/api/deal-screen/${startupId}/rerun`, {
    method: 'POST',
    body: JSON.stringify(modifiers ? { modifiers } : {}),
  });
export const getDealScreen = (startupId) =>
  getJSON(`/api/deal-screen/${startupId}`);
export const getDealScreenStatus = (startupId) =>
  getJSON(`/api/deal-screen/status/${startupId}`);
/**
 * runDealScreen — batch stage-and-analyze call.
 *
 * New (batch) signature — DS_INPUT_TRIGGER_REDESIGN_2026-06-02:
 *   runDealScreen({ stagedInputs, startupId })
 *   stagedInputs: Array<{ id, kind, role, file?, value?, name? }>
 *   Files are appended under key "files"; a JSON "staged_manifest" field carries
 *   the full item list so the backend can pair files by filename and resolve text/url items.
 *
 * Legacy (single-file) back-compat — still accepted during migration:
 *   runDealScreen(file, { startupId, startupName, formData })
 *   When called with a File as first arg, builds the old single-field form.
 */
export const runDealScreen = (fileOrOpts, legacyOpts = {}) => {
  // Detect legacy call: first arg is a File object or null + legacyOpts has formData/startupId
  const isLegacy =
    fileOrOpts instanceof File ||
    fileOrOpts === null ||
    (typeof fileOrOpts !== 'object' || Array.isArray(fileOrOpts));

  if (isLegacy) {
    // Legacy path — single file or pre-built FormData (url/text/name modes)
    const file = fileOrOpts;
    const { startupId, startupName, formData } = legacyOpts;
    const form = formData || new FormData();
    if (!formData) {
      if (file) form.append('file', file);
      if (startupId) form.append('startup_id', startupId);
      if (startupName) form.append('startup_name', startupName);
    }
    return request('/api/deal-screen/run', { method: 'POST', body: form });
  }

  // Batch path — fileOrOpts is { stagedInputs, startupId? }
  const { stagedInputs = [], startupId } = fileOrOpts;
  const form = new FormData();

  // Build manifest: one entry per staged item (files matched by filename)
  const manifest = stagedInputs.map((item) => ({
    id: item.id,
    kind: item.kind,
    filename: item.file ? item.file.name : null,
    value: item.file ? null : (item.value ?? null),
    name: item.name ?? null,
  }));

  // Append every File under the "files" key (multipart list)
  stagedInputs.forEach((item) => {
    if (item.file instanceof File) {
      form.append('files', item.file, item.file.name);
    }
  });

  form.append('staged_manifest', JSON.stringify(manifest));
  if (startupId) form.append('startup_id', startupId);

  return request('/api/deal-screen/run', { method: 'POST', body: form });
};
export const rerunDealScreenSection = (startupId, sectionId, modifiers) =>
  request(`/api/deal-screen/${startupId}/rerun-section`, {
    method: 'POST',
    body: JSON.stringify({ section_id: sectionId, modifiers }),
  });
export const getDealScreenSectionHistory = (startupId, sectionId) =>
  getJSON(`/api/deal-screen/${startupId}/section-history/${sectionId}`);
export const archiveDealScreen = (startupId) =>
  request(`/api/deal-screen/${startupId}/archive`, { method: 'POST' });
// WI-DS-DETECT-002: Startup detection, save deal, and history endpoints
export const detectStartupFromDeck = (file) => {
  const form = new FormData();
  form.append('file', file);
  return request('/api/deal-screen/detect', { method: 'POST', body: form });
};
export const saveDealScreen = (startupId) =>
  request(`/api/deal-screen/${startupId}/save`, { method: 'POST' });
// Reserved for startup history panel — endpoint exists (WI-DS-DETECT-001), UI consumer in Phase 4
export const getDealScreenHistory = (startupId) =>
  getJSON(`/api/deal-screen/${startupId}/history`);
export const getDealScreenWorkItem = (startupId) =>
  getJSON(`/api/deal-screen/${startupId}/work-item`);
export const updateStartupName = (startupId, name) =>
  request(`/api/deal-screen/${startupId}/name`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
export const exportDealScreenPdf = (startupId) =>
  `${base}/api/deal-screen/${startupId}/export-pdf`;
export const exportFCAPdf = (startupId, trigger) =>
  `${base}/api/fca/${encodeURIComponent(startupId)}/export-pdf/${encodeURIComponent(trigger)}`;
// Deal Screen — CEO decision + FCA summary
export const recordDealDecision = (startupId, value) =>
  request(`/api/deal-screen/${startupId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  });
// Deal Screen — Calibration loop (WI-DEAL-SCREEN-CALIBRATION-UI-COMPONENTS-001)
// Authority: docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §14 TC-DS-CAL-01..04.
// Body fields: verdict (required), reason (optional), prose_override (optional, set when CEO refines prose),
// override_source (optional but recommended) ∈ knob_rerun | direct_override | prose_paste.
export const overrideSectionVerdict = (startupId, sectionId, body) =>
  request(`/api/deal-screen/${startupId}/section/${sectionId}/override`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
// WI-4: Undo a section override — reverses the startup override fields + marks the
// learning_signals row as undo_reverted so calibration readers skip it.
export const undoSectionOverride = (startupId, sectionId) =>
  request(`/api/deal-screen/${startupId}/section/${sectionId}/undo-override`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
// WI-4: Persist a CEO prose-refinement delta as a learning_signals row.
// Body fields: agent_prose (original), refined_prose (after CEO edit), time_to_action_seconds.
export const persistProseRefineDelta = (startupId, sectionId, body) =>
  request(`/api/deal-screen/${startupId}/section/${sectionId}/refine-prose`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
// Body fields: outcome (took_call | skipped_call), note (optional).
export const recordCallOutcome = (startupId, body) =>
  request(`/api/deal-screen/${startupId}/call-outcome`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const getFCASummary = (startupId) =>
  getJSON(`/api/fca/${startupId}/summary`);

// LinkedIn Post Studio API (Draft / Approve / Reject / Rejected)
export const getDraftLinkedInPosts = () =>
  getJSON('/api/linkedin-posts/draft');

export const getLinkedInPostRejectReasons = () =>
  getJSON('/api/linkedin-posts/reject-reasons');

// WI-IDEA-CARD-AMENDMENTS-COMPLETION-001 Leg 2 — the seven-code PRE-DRAFT idea-card
// reject vocabulary, each code carrying the two-line definition its button renders.
// A SEPARATE endpoint from the draft-stage reasons above, deliberately: those judge a
// finished post, these judge an idea that has not been written yet. Single authority:
// linkedin_post_rules.json::idea_card_reject_taxonomy (IDEA_CARD_CONTRACT.md §4).
export const getIdeaCardRejectReasons = () =>
  getJSON('/api/linkedin-posts/idea-card-reject-reasons');

// Addendum 1 (30 Oct 2026) — the four owned reasons a rejected COMMENT card offers, `{ codes: [...] }`, the idea-card menu's shape.
// Single authority: reject_reason_digest.comment_reject_codes (a subset of linkedin_post_rules.json::reject_taxonomy).
export const getCommentRejectReasons = () =>
  getJSON('/api/linkedin-posts/comment-reject-reasons');

// WI-IDEA-CARD-BUILD-001 Leg 4 — the pre-draft CEO idea-card gate mode
// ('sampled' | 'mandatory'). Authority: docs/contracts/IDEA_CARD_CONTRACT.md §2h.
// These read/write the SAME config key the drafting pipeline obeys
// (linkedin_post_rules.json::idea_card_gate_mode, via idea_card_gate.py), so the
// on-page toggle cannot be wired to a dead config.
export const getIdeaCardGateMode = () =>
  getJSON('/api/linkedin-posts/idea-card-gate');

// PATCH responds with the mode read BACK through the same reader the pipeline
// uses (read-after-write), so the caller should drive its UI from this response
// rather than from optimistic local state — the response IS the proof the flip
// landed in the config the pipeline reads.
export const setIdeaCardGateMode = (mode) =>
  request('/api/linkedin-posts/idea-card-gate', {
    method: 'PATCH',
    body: JSON.stringify({ mode }),
  });

export const getDraftLinkedInPostCount = () =>
  getJSON('/api/linkedin-posts/draft/count'); // noscan

export const approveLinkedInPost = (postId, ceoCalibration) =>
  request(`/api/linkedin-posts/${postId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ ceo_calibration: ceoCalibration || null }),
  });

export const rejectLinkedInPost = (postId, rejectionReason, ceoCalibration, attribution) =>
  request(`/api/linkedin-posts/${postId}/reject`, {
    method: 'POST',
    body: JSON.stringify({
      ceo_rejection_reason: rejectionReason,
      ceo_calibration: ceoCalibration || null,
      ...(attribution ? { attribution } : {}),
    }),
  });

export const getRejectedLinkedInPosts = () =>
  getJSON('/api/linkedin-posts/rejected');

// The IDEAS half of the Rejected tab (WI-ONE-POST-REACHES-THE-CEO-001 Leg 3, CEO ruling
// D-2026-07-31-15: sub-tabs under REJECTED only, ideas and posts). Its sibling above is the
// posts half. Throws on failure for the same reason getIdeaQueue does — "nothing was rejected"
// and "we could not look" must never render as the same screen.
export const getRejectedIdeas = () =>
  getJSON('/api/linkedin-posts/rejected-ideas');

export const saveLinkedInPost = (postId) =>
  request(`/api/linkedin-posts/${postId}/save`, { method: 'POST' });

export const getSavedLinkedInPosts = () =>
  getJSON('/api/linkedin-posts/saved');

export const overrideLinkedInPost = (postId) =>
  request(`/api/linkedin-posts/${postId}/override`, { method: 'POST' });

// WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 (CEO ruling 2026-07-17) — undo a mark-as-published.
// Distinct from overrideLinkedInPost, which is REJECTED-only ("Only rejected posts can be
// overridden"): this one moves a MARKED post back to Saved and retracts the KB row the mark
// created, so the knowledge base stops asserting a position the CEO never took publicly.
export const unmarkLinkedInPost = (postId) =>
  request(`/api/linkedin-posts/${postId}/unmark-published`, { method: 'POST' });

// WI-LINKEDIN-POST-FRONTEND-V1 — per-card thumbs feedback on the Ranker ordering.
// Backend writes a learning_signals row keyed by agent_type=linkedin_post, source=linkedin_post_ranker_ordering.
export const submitLinkedInPostRankingFeedback = (postId, direction, rankPosition, totalInSession) =>
  request(`/api/linkedin-posts/${postId}/ranking-feedback`, {
    method: 'POST',
    body: JSON.stringify({
      direction,
      rank_position: rankPosition,
      total_in_session: totalInSession,
    }),
  });

// FCA Phase 4: result, history, and trigger endpoints
export const getFCAResult = (startupId, triggerType) =>
  getJSON(`/api/pitch-deck/${encodeURIComponent(startupId)}/fca/${encodeURIComponent(triggerType)}`);
export const getFCAHistory = (startupId, triggerType) =>
  getJSON(`/api/pitch-deck/${encodeURIComponent(startupId)}/fca/${encodeURIComponent(triggerType)}/history`);
export const runPreFCA = (startupId) =>
  request('/api/pitch-deck/run-pre', {
    method: 'POST',
    body: JSON.stringify({ startup_id: startupId }),
  });
export const runFCAPre = runPreFCA;
export const runFCAPost = (startupId, files) => {
  const form = new FormData();
  form.append('startup_id', startupId);
  files.forEach((file) => form.append('transcript_files', file));
  return request('/api/pitch-deck/run-post', { method: 'POST', body: form });
};
export const submitFCAReview = (startupId, trigger, payload) =>
  request(`/api/pitch-deck/${encodeURIComponent(startupId)}/fca/${encodeURIComponent(trigger)}/review`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
export const refineFCAParameter = (startupId, trigger, parameterNumber, knob) =>
  request(`/api/pitch-deck/${encodeURIComponent(startupId)}/fca/${encodeURIComponent(trigger)}/refine`, {
    method: 'POST',
    body: JSON.stringify({ parameter_number: parameterNumber, knob }),
  });
// WI-DESIGN-IMPL-001 Phase 2: knob-aware rerun for the Deal Evaluator FCA long-read.
// Dispatches POST /api/fca/{deal_id}/rerun with scrutiny, research_depth, india_lens.
// Returns { status, deal_id, trigger, changed_parameters }.
export const rerunFcaWithKnobs = (dealId, { scrutiny, researchDepth, indiaLens, trigger = 'PRE' }) =>
  request(`/api/fca/${encodeURIComponent(dealId)}/rerun`, {
    method: 'POST',
    body: JSON.stringify({
      scrutiny,
      research_depth: researchDepth,
      india_lens: indiaLens,
      trigger,
    }),
  });
export const rerunFca = (startupId, trigger) =>
  request(`/api/fca/${encodeURIComponent(startupId)}/rerun/${encodeURIComponent(trigger)}`, {
    method: 'POST',
  });

// ── Deal Sourcing Agent ──────────────────────────────────────────────
export const getDealSourcingDeals = (status = null, limit = 20) => {
  const params = { limit };
  if (status) params.status = status;
  return getJSON('/api/tracxn/deals', params);
};
export const getDealSourcingDeal = (dealId) =>
  getJSON(`/api/tracxn/deals/${dealId}`);
export const passDeal = (dealId, reason = null) =>
  request(`/api/tracxn/deals/${dealId}/pass`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    ...(reason ? { body: JSON.stringify({ pass_reason: reason }) } : {}),
  });
export const getDealSourcingStatus = () =>
  getJSON('/api/tracxn/scan/status');
export const contactDeal = (dealId) =>
  request(`/api/tracxn/deals/${dealId}/contact`, { method: 'POST' });

// Unified Review Actions (Phase 42 Polyfill)
// Maps WorkInbox actions to underlying ReviewBuffer API
export const reviewStartup = async (id, status, rationale) => {
  if (status === 'approved') return acceptReviewItem(id);
  if (status === 'rejected') return rejectReviewItem(id); // backend handles rationale via learning unit? no, backend v1 is simple.
  if (status === 'deferred') return deferReviewItem(id);
};

export const reviewKBEntry = async (id, status, rationale) => {
  if (status === 'approved') return acceptReviewItem(id);
  if (status === 'rejected') return rejectReviewItem(id);
  if (status === 'deferred') return deferReviewItem(id);
};

